import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const ROLE_LABELS = {
  super_admin: 'スーパーアドミン',
  system_admin: 'システム管理者',
  partner_sharoushi: 'パートナー社労士',
};
const ROLE_BADGE = {
  super_admin: 'bg-purple-100 text-purple-700',
  system_admin: 'bg-blue-100 text-blue-700',
  partner_sharoushi: 'bg-emerald-100 text-emerald-700',
};

const SettingsAdmin = () => {
  const isSuper = (localStorage.getItem('admin_role') || '') === 'super_admin';
  const [admins, setAdmins] = useState([]); // [{ email, role, name }]
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [message, setMessage] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminFetch('/api/admin-auth.php?action=settings')
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          const emails = data.settings.allowedEmails || [];
          const roles = data.settings.roles || {};
          const names = data.settings.names || {};
          const rolesLower = {};
          Object.keys(roles).forEach((k) => { rolesLower[k.toLowerCase()] = roles[k]; });
          const namesLower = {};
          Object.keys(names).forEach((k) => { namesLower[k.toLowerCase()] = names[k]; });
          setAdmins(emails.map((e, i) => ({
            email: e,
            role: rolesLower[e.toLowerCase()] || (i === 0 ? 'super_admin' : 'system_admin'),
            name: namesLower[e.toLowerCase()] || '',
          })));
        }
      })
      .catch(() => {});
  }, []);

  const persist = async (list) => {
    setSaving(true);
    setMessage('');
    const roles = {};
    const names = {};
    list.forEach((a) => {
      roles[a.email.toLowerCase()] = a.role;
      if (a.name && a.name.trim()) names[a.email.toLowerCase()] = a.name.trim();
    });
    try {
      const res = await adminFetch('/api/admin-auth.php?action=update-settings', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ allowedEmails: list.map((a) => a.email), roles, names }),
      });
      const data = await res.json();
      if (data.success) {
        setAdmins(list);
        setMessage('保存しました');
        setTimeout(() => setMessage(''), 2000);
      } else {
        setMessage(data.message || '保存に失敗しました');
      }
    } catch { setMessage('保存に失敗しました'); }
    setSaving(false);
  };

  const addEmail = () => {
    const email = newEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) { setMessage('正しいメールアドレスを入力してください'); return; }
    if (admins.some((a) => a.email.toLowerCase() === email)) { setMessage('このメールアドレスは既に登録されています'); return; }
    // スーパーアドミンはシステム管理者として追加。システム管理者はパートナー社労士として追加（権限は後から変更可）。
    persist([...admins, { email, name: newName.trim(), role: isSuper ? 'system_admin' : 'partner_sharoushi' }]);
    setNewEmail('');
    setNewName('');
  };

  const changeRole = (email, role) => {
    persist(admins.map((a) => (a.email === email ? { ...a, role } : a)));
  };

  // 名前は入力中はローカル更新、フォーカスを外した時に保存
  const setLocalName = (email, name) => setAdmins(admins.map((a) => (a.email === email ? { ...a, name } : a)));
  const commitNames = () => persist(admins);

  const removeEmail = (email) => {
    if (admins.length <= 1) { setMessage('最低1つのメールアドレスが必要です'); return; }
    const remaining = admins.filter((a) => a.email !== email);
    if (!remaining.some((a) => a.role === 'super_admin')) {
      setMessage('スーパーアドミンを最低1人残してください');
      return;
    }
    persist(remaining);
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">設定</h2>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      <div className="bg-white rounded-xl shadow p-6">
        <h3 className="font-bold text-lg mb-2">管理者アカウント</h3>
        <p className="text-xs text-gray-500 mb-4">
          ここに登録されたメールアドレスのみ管理画面にログインできます。
          <br />・<b>スーパーアドミン</b>：全機能＋この設定（管理者の追加・権限変更）が可能
          <br />・<b>システム管理者</b>：全機能を操作可。この設定では<b>スーパーアドミン以外の追加・権限変更・削除</b>が可能（スーパーアドミンは変更不可）
          <br />・<b>パートナー社労士</b>：レポート管理のみ・自分の担当顧客だけ閲覧／操作可
          {!isSuper && <><br /><span className="text-amber-600">※ あなたはシステム管理者です。スーパーアドミンの変更はできません（それ以外の権限変更は可能）。</span></>}
        </p>

        <div className="flex gap-2 mb-5">
          <input type="text" value={newName} onChange={(e) => setNewName(e.target.value)}
            placeholder="名前（例：古藤 大）"
            className="w-40 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && addEmail()} />
          <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
            placeholder={isSuper ? 'メールアドレスを追加（システム管理者として追加）' : 'パートナー社労士のメールアドレスを追加'}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            onKeyDown={(e) => e.key === 'Enter' && addEmail()} />
          <button onClick={addEmail} disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">追加</button>
        </div>

        <div className="space-y-2">
          {admins.map((a) => (
            <div key={a.email} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2 gap-3">
              <input type="text" value={a.name || ''} onChange={(e) => setLocalName(a.email, e.target.value)} onBlur={commitNames}
                disabled={saving} placeholder="名前"
                className="w-32 shrink-0 border border-gray-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-blue-400" />
              <span className="text-sm flex-1 truncate text-gray-600">{a.email}</span>
              <span className={`text-[10px] font-bold rounded-full px-2 py-0.5 shrink-0 ${ROLE_BADGE[a.role] || 'bg-gray-100 text-gray-600'}`}>
                {ROLE_LABELS[a.role] || a.role}
              </span>
              <select value={a.role} onChange={(e) => changeRole(a.email, e.target.value)}
                disabled={saving || (!isSuper && a.role === 'super_admin')}
                className="border border-gray-300 rounded-lg px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 disabled:bg-gray-100 disabled:text-gray-400">
                {(isSuper || a.role === 'super_admin') && <option value="super_admin">{ROLE_LABELS.super_admin}</option>}
                <option value="system_admin">{ROLE_LABELS.system_admin}</option>
                <option value="partner_sharoushi">{ROLE_LABELS.partner_sharoushi}</option>
              </select>
              <button onClick={() => removeEmail(a.email)} disabled={saving || (!isSuper && a.role === 'super_admin')}
                className="bg-red-50 text-red-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed">削除</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SettingsAdmin;
